import { z } from "zod";
import { EntityType } from "../entityType.js";
import { StackPath, stackPathName } from "../generated/storyblok/stackPaths.js";
import { zodJsonStringAsDate } from "../utils/schema.js";
import { activityVerbs } from "./Activity.js";
import { CouponSchema } from "./Coupon.js";
import { IdentitySchema } from "./Identity.js";
import { NoanIntelligenceScoreResponseSchema } from "./NoanML.js";
import { ToneDefinitionIdSchema } from "./ToneDefinition.js";

export const defaultOrganizationName = "New Project";
/**
 * Default stacks populated when a new organization is created
 */
export const defaultOrganizationStacks = [StackPath.Foundations];
/**
 * Non deletable stacks
 */
export const lockedStacks = [StackPath.Foundations];

export enum WidgetId {
  Tasks = "tasks",
  SitePerformance = "site-performance",
  DocumentAnalysis = "document-analysis",
  AdvisorPortal = "advisor-portal",
  AdvisorHub = "advisor-hub",

  /** @deprecated */
  Build = "build",
  /** @deprecated */
  Create = "create",
  /** @deprecated */
  Network = "network",
  /** @deprecated */
  Team = "team",
  /** @deprecated */
  Sales = "sales",
  /** @deprecated */
  ProjectSummary = "project-summary",
  /** @deprecated */
  News = "news",
}

export enum TargetType {
  B2B = "B2B",
  B2C = "B2C",
}

/** @deprecated */
export enum UsageMode {
  App = "app",
  Headless = "headless",
  Both = "both",
  NotSure = "not-sure",
}

export enum BusinessType {
  Solo = "Solo",
  Team = "Team",

  /** @deprecated */
  Agency = "Agency",
}

export enum BusinessGoals {
  CreateContent = "Create content",
  DevelopMarketingMaterials = "Develop marketing materials",
  BuildSubscriptionModel = "Build a subscription model",
  DevelopOurBrand = "Develop our brand",
  BuildATeam = "Build a team",
  DevelopAVision = "Develop a vision",
}

export enum TeamSize {
  One = 1,
  Small = 9, // 2-9
  Medium = 49, // 10-49
  Big = 249, // 50-249
  Huge = 2500, // 250-2500
}

export enum Industry {
  Healthcare = "Health and Wellness",
  FinanceAndBanking = "Finance and Banking",
  Education = "Education",
  RetailAndECommerce = "Retail, DTC, and E-commerce",
  RealEstate = "Real Estate",
  Manufacturing = "Manufacturing",
  HospitalityAndTravel = "Hospitality and Travel",
  MediaAndEntertainment = "Media and Entertainment",
  Tech = "Technology and IT Services",
  Logistics = "Logistics and Supply Chain",
  ProfessionalServices = "Professional Services",
  Other = "Other",
}

export enum BrandTone {
  Neutral = "neutral",
  Playful = "playful",
  Professional = "professional",
  Informative = "informative",
  Authorative = "authorative",
  Humourous = "humourous",
}

export enum BrandToneDescription {
  Neutral = "Use balanced, objective language without emotional bias or strong opinions. Write in clear, straightforward sentences using factual statements and neutral vocabulary. Avoid superlatives, promotional language, or persuasive techniques. Present information without taking sides or showing preference. Maintain a calm, measured tone that focuses on delivering content without personality or agenda.",
  Playful = "Write with lighthearted energy using creative wordplay, casual expressions, and fun analogies. Include playful metaphors, gentle humor, and conversational asides. Use exclamation points sparingly but effectively. Employ creative formatting, engaging questions, and interactive elements. Avoid being overly silly or unprofessional - maintain substance while keeping the tone enjoyable and spirited.",
  Professional = "Use formal business language with proper grammar, complete sentences, and industry-appropriate terminology. Write with authority and competence, focusing on expertise and credibility. Avoid contractions, slang, or overly casual expressions. Structure content logically with clear conclusions and actionable insights. Maintain respect for the audience while demonstrating knowledge and reliability.",
  Informative = "Focus on delivering clear, comprehensive information using educational language and structured explanations. Break down complex topics into digestible parts with logical flow. Include relevant details, examples, and context to enhance understanding. Use transitional phrases and organized formatting. Prioritize clarity and completeness over personality, ensuring the audience learns and understands the subject matter.",
  Authoritative = 'Write with commanding confidence using definitive statements and expert-level knowledge. Assert expertise through specific insights, industry leadership, and decisive recommendations. Use strong, direct language that establishes credibility and demonstrates mastery. Avoid uncertainty phrases like "might" or "perhaps." Present information as established fact backed by experience and proven results.',
  Humorous = "Incorporate wit, clever observations, and appropriate jokes while maintaining relevance to the topic. Use timing, wordplay, and unexpected comparisons to create amusement. Include light sarcasm, amusing examples, or funny scenarios that enhance rather than distract from the message. Avoid offensive humor or jokes that could alienate the audience. Keep humor purposeful and tied to the content goals.",
}

/**
 * Steps in the onboarding flow.
 */
export enum OnboardingStep {
  COMPANY_NAME = "company-name",
  CHOICE = "choice",
  WEBSITE_SCANNING = "website-scanning",
  SETUP_COMPLETE = "setup-complete",

  /** @deprecated */
  USAGE_MODE = "usage-mode",
  /** @deprecated */
  GETTING_STARTED = "getting-started",
  /** @deprecated */
  BRAND_TONE = "brand-tone",
  /** @deprecated */
  BUSINESS_VISION = "business-vision",
  /** @deprecated */
  FIRST_CONTACT = "first-contact",
}

/**
 * List of valid file types for image upload.
 */
export const supportedLogoMimeTypes = [
  "image/png", // .png
  "image/jpeg", // .jpeg
] as const;

/** @deprecated Nothing reads `enabled`, see the `blockPaths` setting */
export const BlockPathConfigurationSchema = z.object({
  enabled: z.boolean().default(true),
});

export const LogoSchema = z.object({
  storagePath: z.string(),
  originalFileName: z.string(),
  uploadedAt: z.string(),
});

export const OnboardingProgressSchema = z.object({
  /**
   * Current step in the onboarding flow.
   * This allows the user to resume progress after refreshing the page or
   * reconnecting.
   */
  currentStep: z.enum(OnboardingStep),
  /** @deprecated How the user intended to primarily use NOAN */
  usageMode: z.enum(UsageMode).optional(),
  /**
   * The website URL provided by the user, used for scraping facts.
   * We keep it here, even though it’s also saved as a fact, so we can re-run the
   * scraping whenever the website value changes.
   */
  websiteUrl: z.string().optional(),
  /** Timestamp when the website scraping started */
  factScraperStartedAt: z.string().optional(),
  /** Timestamp when the website scraping finished */
  factScraperFinishedAt: z.string().nullish(),
  /** Wether the scraping finished with a failure */
  factScraperFailed: z.boolean().optional(),

  /** @deprecated - Idea generated after website scraping saved as first note */
  firstNote: z.string().optional(),
  /** @deprecated - Number of documents uploaded by the user during the facts base step */
  documentsUploaded: z.number().optional(),
  /** @deprecated Only used by the legacy OnboardingWizard flow. The company business vision */
  businessVision: z.string().optional(),
  /**
   * @deprecated Only used by the legacy OnboardingWizard flow.
   * Chat thread ID used for the end of the onboarding to show a review of the
   * collected facts and indicate the next steps
   */
  chatThreadId: z.string().optional(),
  /** @deprecated Only used by the legacy OnboardingWizard flow. */
  firstContact: z.string().optional(),
});

export const ActionablePromptSchema = z.object({
  title: z.string().max(64).describe("A concise title summarizing the prompt"),
  prompt: z
    .string()
    .describe(
      "A clear, actionable prompt the user can click to execute immediately",
    ),
  entityType: z
    .enum(EntityType)
    .describe(
      "The primary entity type this prompt relates to (e.g., 'contact' for contact network management, 'note' for notes, 'task' for tasks, 'asset' for content generation, etc.)",
    ),
  reasoning: z
    .string()
    .nullable()
    .describe(
      "Brief explanation of why this specific prompt is relevant based on the company context",
    ),
});

export const defaultTaskBoardColumns: TaskBoardColumn[] = [
  { id: "backlog", title: "Backlog", position: 1000 },
  { id: "in-progress", title: "In Progress", position: 2000 },
  { id: "done", title: "Done", position: 3000 },
];

export function getTaskBoardColumnTitle(columnId: string) {
  return defaultTaskBoardColumns.find((col) => col.id === columnId)?.title;
}

export const TaskBoardColumnSchema = z.object({
  id: z.string(),
  title: z.string(),
  position: z.number(),
});

export const TaskBoardSchema = z.object({
  columns: z.array(TaskBoardColumnSchema).optional(),
});

export const WebhooksConfigurationSchema = z.object({
  enabled: z.boolean().optional().default(false),

  /**
   * A valid URL to which webhook events will be sent.
   */
  endpoint: z.url().nullable().optional(),

  /**
   * If enabled, the endpoint certificate must be valid for the request to
   * be complete.
   */
  verifyCertificate: z.boolean().optional().default(true),

  /**
   * A string used for webhook request verification.
   */
  signature: z.string().nullable().optional(),

  /**
   * A list of events for which webhooks will be delivered.
   */
  subscriptions: z.array(z.enum(activityVerbs)).optional().default([]),
});

export const OrganizationSettingsSchema = z.object({
  brandTone: z.enum(BrandTone).optional(),
  defaultToneDefinitionId: ToneDefinitionIdSchema.optional().nullable(),
  requireFactChangeApproval: z.boolean().optional(),
  attributes: z
    .object({
      newCompany: z.boolean().optional(),
      website: z.url().nullable().optional(),
      industry: z.enum(Industry).optional(),
      teamSize: z.enum(TeamSize).optional(),
      targetType: z.enum(TargetType).optional(),
      businessGoals: z.array(z.enum(BusinessGoals)).optional(),
      industryStacks: z
        .array(
          z.union([
            z.enum(stackPathName),
            /**
             * z.string() is needed here to be compatible with values that are not part of `stackPathName`.
             * e.g. `Other`, or other old static values used in the first versions of the onboarding.
             */
            z.string(),
          ]),
        )
        .optional(),
      businessType: z.enum(BusinessType).optional(),
    })
    .optional(),

  websiteScraper: z
    .object({
      // We don't really care about having these be real dates for our purposes.
      startedAt: z.string().optional(),
      finishedAt: z.string().optional().nullable(),
      website: z.url().optional(),
    })
    .optional(),

  creatorPrompts: z
    .object({
      /**
       * Prisma returns this as a string, since it's a JSON value and not a db column.
       * We need to pre-process it first:
       */
      generatedAt: zodJsonStringAsDate,
      suggestions: z.array(z.string()),
    })
    .optional(),

  actionablePrompts: z
    .object({
      startedAt: zodJsonStringAsDate.nullish(),
      generatedAt: zodJsonStringAsDate.optional(),
      prompts: z.array(ActionablePromptSchema).optional(),
    })
    .optional(),

  /**
   * Stacks whose blocks are used in the context of the interaction
   * with the assistants or any kind of AI generation.
   */
  stacks: z.array(z.string()).optional(),

  /**
   * @deprecated The `blocks` table holds a row per organization block, so block
   * configuration belongs there. Nothing reads the `enabled` flag stored here.
   *
   * Maps blockPath -> blockPath options
   */
  blockPaths: z
    .record(z.string(), BlockPathConfigurationSchema)
    .default({})
    .optional(),

  /** @deprecated Widget displayed in the dashboard */
  widgets: z.array(z.enum(WidgetId)).optional(),

  /**
   * Theme settings: user-configurable UI look & feel (colors, logo, etc.)
   */
  theme: z
    .object({
      colorPrimary: z.string().optional(),
      colorSecondary: z.string().optional(),
      logo: LogoSchema.optional(),
    })
    .optional(),

  advisorMode: z
    .object({
      isEnabled: z.boolean().optional(),
    })
    .optional(),

  guestMode: z
    .object({
      isEnabled: z.boolean().optional(),
      colorPrimary: z.string().optional(),
      colorSecondary: z.string().optional(),
      coverImage: LogoSchema.optional().describe(
        "Illustration for the side panel on the guest login, signup and onboarding pages.",
      ),
      website: z
        .string()
        .optional()
        .describe(
          "An event website or the host website to redirect to after the onboarding is completed.",
        ),
      promoCode: z
        .string()
        .optional()
        .describe("A promotion code used for a subscription discount."),
      trialDays: z
        .number()
        .optional()
        .describe("Number of trial days for a subscription."),
      selfSignup: z
        .boolean()
        .optional()
        .describe(
          "Lets the public signup page add a guest to this org. Off means guests are added through the API only.",
        ),
      autoTransfer: z
        .boolean()
        .optional()
        .describe(
          "Runs the transfer to the guest's own org on first login instead of showing the guest form.",
        ),
    })
    .optional(),

  onboardingProgress: OnboardingProgressSchema.optional(),

  assessment: z
    .object({
      intelligenceScore:
        NoanIntelligenceScoreResponseSchema.shape.intelligenceScore.optional(),
      scoreLevel: NoanIntelligenceScoreResponseSchema.shape.level.optional(),
      summary:
        NoanIntelligenceScoreResponseSchema.shape.organizationSummary.optional(),
      lastSyncedAt: z.string().optional(),
    })
    .optional(),

  /**
   * Task board configuration: defines columns and their ordering
   */
  taskBoard: TaskBoardSchema.optional(),

  /**
   * Defines webhook configuration, including endpoints and subscribed
   * events.
   */
  webhooks: WebhooksConfigurationSchema.optional(),
});

export const OrganizationSchema = z.object({
  id: z.uuid(),
  slug: z.string().min(3).max(64),
  name: z.string().min(3).max(128),
  settings: OrganizationSettingsSchema.default({}),
});

export const OrganizationWithIdentitiesSchema = z.object({
  ...OrganizationSchema.shape,
  coupons: z.array(CouponSchema).optional(),
  identities: z.array(IdentitySchema),
  logoUrl: z.string().optional(),
});

export const CreateOrganizationSchema = OrganizationSchema.pick({
  name: true,
  settings: true,
});

export const UpdateOrganizationSchema = z.object({
  ...CreateOrganizationSchema.shape,
  name: CreateOrganizationSchema.shape.name.optional(),
  settings: CreateOrganizationSchema.shape.settings.optional(),
});

/**
 * Metadata required to start the upload procedure
 */
export const LogoUploadMetadataSchema = z.object({
  mimeType: z
    .string()
    .refine(
      (v) => (supportedLogoMimeTypes as unknown as string[]).includes(v),
      {
        error: "Not a valid input mime type",
      },
    ),
  fileName: z.string().max(1024),
});

export const CommitLogoSchema = z.object({
  ...LogoUploadMetadataSchema.shape,
  storagePath: z.string(),
});

export type Organization = z.infer<typeof OrganizationSchema>;
export type OrganizationWithIdentities = z.infer<
  typeof OrganizationWithIdentitiesSchema
>;

export type OrganizationSettings = z.infer<typeof OrganizationSettingsSchema>;
export type OnboardingProgress = z.infer<typeof OnboardingProgressSchema>;
export type ActionablePrompt = z.infer<typeof ActionablePromptSchema>;
export type TaskBoardColumn = z.infer<typeof TaskBoardColumnSchema>;
export type TaskBoard = z.infer<typeof TaskBoardSchema>;
export type WebhooksConfiguration = z.infer<typeof WebhooksConfigurationSchema>;

export type LogoUploadMetadata = z.infer<typeof LogoUploadMetadataSchema>;
export type CommitLogo = z.infer<typeof CommitLogoSchema>;
export type Logo = z.infer<typeof LogoSchema>;
